import React, { useState, useEffect, memo, useMemo } from "react";
import ValuesStore from "../../store/values-store";
import { useNavigate, useLocation } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import { Space, Button, Card, theme, Col, Row } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import useTable from "../../hooks/table";

import useAdd from "../../hooks/add";
import useDelete from "../../hooks/rest_delete";
import Settings from "../../dependencies/custom/settings";
import CustomFunctions from "../../dependencies/custom/custom-functions";
import useDrawer from "../../hooks/drawer";
import useExcel from "../../hooks/excel";

const Setup = ({
  label,
  tableName,
  columns,
  saveEndpoint,
  getEndpoint,
  keyName,
  split,
  recordToEdit,
  deleteResponse,
  setRecordToEdit,
  itemToView,
  headingKey,
  setItemToview,
  viewJSX,
  httpMethod,
  resultKey,
  editKey = "id",
  extraButtons,
  leadingButtons,
  extraJSX,
  rerender,
}) => {
  // console.log(itemToView)
  const location = useLocation();
  const { token } = theme.useToken();
  const valuesStore = ValuesStore();
  const add = useAdd("tables_metadata", "table_name");
  const del = useDelete();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const drawer = useDrawer();
  const excel = useExcel();
  const [columnHeadings, setColumnHeadings] = useState(
    Utilities.createArrayFromSingleObjectKey(columns, "title"),
  );

  const perms = useMemo(() => {
    return valuesStore.getValue("permissions");
  }, []);

  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    keyName || "id",
    {},
    {},
  );

  async function bootstrap() {
    table.setData([]);

    const hasPerm = Utilities.hasPermissionV2(["GET"], perms, getEndpoint);

    if (!hasPerm && !hasBootstrapped) {
      Utilities.showNotification(
        undefined,
        `Permission to fetch data is denied (${getEndpoint})`,
      );
      return;
    }
    table.setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      getEndpoint,
      undefined,
      navigate,
    );

    if (res?.ok) {
      let d = [];
      if (resultKey) {
        d = res?.data?.[resultKey];
      } else {
        d = res?.data;
      }
      table.setData(d);
      if (d.length) {
        setColumnHeadings(Object.keys(d[0]));
      }
    } else {
      if (res?.msg) Utilities.showNotification(undefined, res?.msg);
    }
    table.setLoading(false);
  }

  async function addOnOk() {
    if (!saveEndpoint) return;

    const a = add.validateShowErrorMessage();
    if (!a?.isValid) return;
    let data = { ...add.record };

    if (split && Array.isArray(split)) {
      for (let item of split) {
        data = {
          ...data,
          [item]: add?.record?.[item]
            ?.split(",")
            ?.map((v) => parseInt(v)) /*, id: parseInt(add?.record?.id)*/,
        };
      }
    }

    for (let key in data) {
      if (typeof data[key] == "string" && Utilities.isNumeric(data[key])) {
        data[key] = Number(data[key]);
      }
    }

    const method = httpMethod ? httpMethod : data?.isEdit ? "PUT" : "POST";
    const s =
      method == "PUT"
        ? `${saveEndpoint}/${add?.record?.[editKey]}`
        : saveEndpoint;

    setLoading(true);
    add.setLoading(true);
    const cleaned = Utilities.cleanupObject(data);
    if (
      tableName === "ACCOMMODATION" &&
      data.category === "House" &&
      (add.record.capacity == null || String(add.record.capacity).trim() === "")
    ) {
      cleaned.capacity = null;
    }
    const res = await Utilities.fetchWithAuth(method, s, cleaned, navigate);
    if (res?.ok) {
      await bootstrap();
      add.setShowModal(false);
      setRecordToEdit(undefined);
    } else {
      Utilities.showNotification(undefined, res.msg);
    }
    setLoading(false);
    add.setLoading(false);
  }

  useEffect(() => {
    if (itemToView) {
      //for viewing
      drawer.setPlacement("right");
      drawer.setWidth(500);
      drawer.setOpen(true);
      drawer.setTitle(itemToView?.[headingKey] || "Record");
    }
  }, [itemToView]);

  useEffect(() => {
    setHasBootstrapped(false);
    add.setShowModal(false);
    add.setHttpMethod("GET");
  }, [location.pathname]);

  useEffect(() => {
    if (rerender !== undefined) {
      bootstrap();
    }
  }, [rerender]);

  useEffect(() => {
    if (deleteResponse?.ok) {
      bootstrap();
    } else {
      Utilities.showNotification(undefined, deleteResponse?.msg);
    }

    if (recordToEdit) {
      add.setModalHeading("Edit record");
      add.setTblName(tableName);
      add.setMaxSelectCount(1);
      add.setRecord({
        ...Utilities.convertObjectValsToString(recordToEdit),
        ...(tableName === "ACCOMMODATION"
          ? {
              capacity:
                recordToEdit.capacity == null
                  ? ""
                  : String(recordToEdit.capacity),
            }
          : {}),
        isEdit: true,
      });
      add.setShowModal(true);
    }

    if (!hasBootstrapped) {
      table.setColumns(columns || []);
      bootstrap();
      setHasBootstrapped(true);
    }

    return () => {
      setRecordToEdit?.(undefined);
    };
  }, [
    columns,
    location.pathname,
    hasBootstrapped,
    deleteResponse,
    recordToEdit,
    tableName,
  ]);

  function addNew() {
    add.setMaxSelectCount(undefined);
    add.reset();
    add.setTblName(tableName);
    add.setShowModal(true);
  }

  function exportToExcel() {
    excel.exportXLSX(
      columnHeadings,
      table.data,
      "sheet1",
      `${Utilities.formatDate(new Date()?.toDateString())}_${tableName}.xlsx`,
    );
  }

  return (
    <>
      <div className="">
        <Row gutter={[16, 0]}>
          <Col xs={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace p-2"
              title={label}
              extra={
                <Space wrap>
                  {Utilities.hasPermissionV2(
                    ["POST", "PUT", "PATCH"],
                    perms,
                    saveEndpoint,
                  ) ? (
                    <Button
                      loading={loading}
                      type="primary"
                      className="border-0"
                      onClick={addNew}
                      icon={<i className="fas fa-plus" />}
                    >
                      Add New Record
                    </Button>
                  ) : (
                    ""
                  )}
                  {leadingButtons}
                  {extraButtons}
                  <Button
                    loading={loading}
                    onClick={exportToExcel}
                    icon={<DownloadOutlined />}
                  >
                    Export
                  </Button>
                </Space>
              }
            >
              <div className="table-responsivex">
                {table.tableWithHeaderFooter(
                  CustomFunctions.tblCaption,
                  CustomFunctions.tblCaption,
                  { scroll: { x: "max-content" } },
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      {extraJSX}
      {saveEndpoint
        ? add.addModal(
            "Add New Item",
            addOnOk,
            undefined,
            undefined,
            undefined,
            false,
          )
        : ""}
      {drawer.drawerJSX(1000, <>{viewJSX?.(itemToView)}</>)}
    </>
  );
};

export default memo(Setup);
